/**
 * De voorgerenderde pagina vasthouden tot React klaar is om te tekenen.
 *
 * Het probleem dat dit oplost: onze landingspagina's staan volledig uitgeschreven in de HTML, dus
 * de bezoeker ziet ze meteen. Daarna start React met `createRoot`, en dat **leegt eerst de
 * container** voordat het zelf iets tekent. De route zit achter `lazy()`, dus die eerste tekenbeurt
 * is de Suspense-fallback — en die was leeg. Resultaat: pagina zichtbaar → wit → pagina weer terug.
 * Precies de "glitch" die je ziet als je vanuit Google binnenkomt.
 *
 * De oplossing is niet sneller laden, maar niets weggooien: we bewaren de HTML die al op het scherm
 * stond en gebruiken die als Suspense-fallback. De bezoeker ziet dan onafgebroken dezelfde pagina,
 * die op het moment dat de route klaar is wordt vervangen door de echte, interactieve versie.
 *
 * Alleen voor de eerste route ná het opstarten, en alleen voor het pad waarop we binnenkwamen: bij
 * doorklikken hoort de gewone laadindicator, anders zou je de vórige pagina blijven zien.
 */
let snapshot: string | null = null;
let startPad: string | null = null;

/** Vlak vóór createRoot aanroepen, mét de HTML die al in #root stond. */
export function bewaarEerstePaint(html: string, pad: string): void {
  // Een lege of piepkleine snapshot (artikelpagina's krijgen alleen een kop mee) heeft geen zin:
  // dan is er niets om vast te houden en tonen we gewoon de normale laadindicator.
  if (html.trim().length < 1000) return;
  snapshot = html;
  startPad = pad;
}

/** De bewaarde HTML, of null als er niets (meer) te tonen is voor dit pad. */
export function eerstePaintHtml(pad: string): string | null {
  return snapshot && pad === startPad ? snapshot : null;
}

/** Aanroepen zodra de echte route getekend is: de snapshot mag weg (en scheelt ~150 kB geheugen). */
export function vergeetEerstePaint(): void {
  snapshot = null;
  startPad = null;
}
